import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../../../../components/Modal';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../components/Notification';
import HttpStatus from '../../../../../enums/httpStatus';
import { mobileOrderStatusApi } from '../../../../../services/requests';
import * as TP from '../../../../../types/apiResponseTypes';

type Props = {
  closeModal: () => void;
  reqGetAllOrderStatuses: () => void;
  orderStatusSelected: TP.MobileOrderStatus;
};

const EditOrderStatus = ({ closeModal, reqGetAllOrderStatuses, orderStatusSelected }: Props) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [descricao, setDescricao] = useState('');
  const [isUltimo, setIsUltimo] = useState<'s' | 'n'>('n');
  const [cor, setCor] = useState('');
  const [legenda, setLegenda] = useState('');
  const [exibeListaPedido, setExibeListaPedido] = useState<'s' | 'n'>('n');
  const [peso, setPeso] = useState('0');

  useEffect(() => {
    setDescricao(orderStatusSelected.descricao);
    setIsUltimo(orderStatusSelected.is_ultimo || 'n');
    setCor(orderStatusSelected.cor || '');
    setLegenda(orderStatusSelected.legenda || '');
    setExibeListaPedido(orderStatusSelected.exibe_lista_pedido || 'n');
    setPeso(String(orderStatusSelected.peso ?? 0));
  }, [orderStatusSelected]);

  const reqUpdateOrderStatus = async () => {
    setIsLoading(true);

    try {
      const res = await mobileOrderStatusApi.update(
        {
          descricao,
          is_ultimo: isUltimo,
          cor: cor || null,
          legenda: legenda || null,
          exibe_lista_pedido: exibeListaPedido,
          peso: peso === '' ? null : Number(peso),
        },
        orderStatusSelected.id,
      );

      if (res.status === HttpStatus.OK) {
        frontendNotification({
          message: t('successfullyUpdated'),
          type: 'success',
        });

        closeModal();
        reqGetAllOrderStatuses();
      } else {
        throw res;
      }
    } catch (err) {
      if (!mobileOrderStatusApi.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title={t('editOrderStatus')}
      showModal
      closeModal={closeModal}
      action={() => reqUpdateOrderStatus()}
      isLoading={isLoading}
    >
      <div className="form">
        <div className="form-row">
          <div className="form-row-item">
            <label htmlFor="mobileOrderStatusSiglaEdit">{t('sigla') as string}</label>
            <input
              id="mobileOrderStatusSiglaEdit"
              type="text"
              value={orderStatusSelected.sigla || ''}
              disabled
            />
          </div>
          <div className="form-row-item">
            <label htmlFor="mobileOrderStatusDescriptionEdit">{t('description') as string}</label>
            <input
              id="mobileOrderStatusDescriptionEdit"
              type="text"
              maxLength={100}
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              disabled={isLoading}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-row-item">
            <label htmlFor="mobileOrderStatusFinalEdit">{t('isFinalStatus') as string}</label>
            <select
              id="mobileOrderStatusFinalEdit"
              className="select"
              value={isUltimo}
              onChange={(e) => setIsUltimo(e.target.value as 's' | 'n')}
              disabled={isLoading}
            >
              <option value="n">{t('no')}</option>
              <option value="s">{t('yes')}</option>
            </select>
          </div>
          <div className="form-row-item">
            <label htmlFor="mobileOrderStatusVisibleEdit">{t('showInOrderList') as string}</label>
            <select
              id="mobileOrderStatusVisibleEdit"
              className="select"
              value={exibeListaPedido}
              onChange={(e) => setExibeListaPedido(e.target.value as 's' | 'n')}
              disabled={isLoading}
            >
              <option value="n">{t('no')}</option>
              <option value="s">{t('yes')}</option>
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-row-item">
            <label htmlFor="mobileOrderStatusColorEdit">{t('statusColor') as string}</label>
            <input
              id="mobileOrderStatusColorEdit"
              type="text"
              maxLength={100}
              value={cor}
              onChange={(e) => setCor(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div className="form-row-item">
            <label htmlFor="mobileOrderStatusLegendEdit">{t('statusLegend') as string}</label>
            <input
              id="mobileOrderStatusLegendEdit"
              type="text"
              maxLength={200}
              value={legenda}
              onChange={(e) => setLegenda(e.target.value)}
              disabled={isLoading}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-row-item">
            <label htmlFor="mobileOrderStatusWeightEdit">{t('weight') as string}</label>
            <input
              id="mobileOrderStatusWeightEdit"
              type="number"
              step="1"
              value={peso}
              onChange={(e) => setPeso(e.target.value)}
              disabled={isLoading}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EditOrderStatus;
